import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, Typography } from 'antd';

import { A, navigate } from 'hookrouter';
import classNames from 'classnames';
import { BulbFilled } from '@ant-design/icons';

import { selectProjectUuid } from '../../views/Project/projectSlice';
import { usePermission } from '../../functions/permissionValidator';
import { routeActiveKey } from './sidebarSlice';

import routes from '../../routes.js';

const { Text } = Typography;

const SidebarItemsFromRoutes = (props) => {
  const dispatch = useDispatch();
  const projectUuid = useSelector(selectProjectUuid);

  const { classes, sidebarCollapsed } = props;

  const active = useSelector((state) => state.sidebar.activeKey);

  const {
    isGlobalAdmin,
    canEdit,
    canAccessResource,
    canManageSharedConfigs,
    canManageSharedFields,
  } = usePermission();

  // route.permission -> predicate. Each of these backend flags gates both read and
  // write of its resource, so one predicate per resource is enough.
  const permissionChecks = {
    sharedConfigs: canManageSharedConfigs,
    sharedFields: canManageSharedFields,
  };

  // Filter routes based on user permissions
  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      // Global admin can see all routes
      if (isGlobalAdmin()) {
        return true;
      }

      // globalAdminOnly routes are only for is_admin: true users
      if (route.globalAdminOnly) {
        return false;
      }

      // Routes gated by a named user permission flag. Checked before adminOnly so the
      // specific flag wins if a route ever carries both.
      if (route.permission) {
        const check = permissionChecks[route.permission];
        return check ? check() : false;
      }

      // adminOnly routes require project admin role
      if (route.adminOnly) {
        return canEdit();
      }

      // For other routes, check resource access
      if (route.resource) {
        return canAccessResource(route.resource);
      }

      // Default: allow access
      return true;
    });
  }, [
    isGlobalAdmin,
    canEdit,
    canAccessResource,
    canManageSharedConfigs,
    canManageSharedFields,
  ]);

  // Derive the active route path from the current URL.
  // Recomputes whenever `active` changes (dispatched on navigation and on startup).
  const activeRoutePath = useMemo(() => {
    const parts = window.location.pathname.split('/').slice(2);
    while (parts.length) {
      const path = '/' + parts.join('/');
      if (routes.find((r) => r.path === path)) return path;
      parts.pop();
    }
    return '';
  }, [active]);

  return (
    <div className={classes.sidebarWrapper}>
      <Menu
        className={classes.list}
        mode="inline"
        inlineCollapsed={sidebarCollapsed}
        selectedKeys={activeRoutePath ? [activeRoutePath] : []}
      >
        {filteredRoutes.map((route) => {
          const targetRoute = '/' + projectUuid + route.path;

          return (
            <Menu.Item
              key={route.path}
              icon={<route.icon className={classes.itemIcon} />}
              className={
                ' menu-link-li ' +
                `${classNames(classes.item, {
                  [classes.itemActive + ' menu-link-li-active ']:
                    route.path === activeRoutePath,
                })}`
              }
              onClick={() => {
                navigate(targetRoute);
                dispatch(routeActiveKey());
              }}
            >
              <A href={targetRoute} className={classes.itemLink}>
                <Text className={classes.itemText}>{route.name}</Text>
              </A>
            </Menu.Item>
          );
        })}
      </Menu>

      <div className="updates-box">
        <span>
          <BulbFilled />
        </span>
        <p>Version 2.1.4</p>
        <a href={projectUuid + '/version'}>Learn More</a>
      </div>
    </div>
  );
};

export default SidebarItemsFromRoutes;
